/**
 * Gider pusulası — kâğıt koçanın birebir görünümü.
 *
 * Kullanıcı isteği (02.10.2026): "yeni pusula ekranı tam görseldeki gibi
 * olsun". Bu yüzden form alanları serbest bir düzende değil, belgenin kendi
 * yerleşiminde duruyor; şube elle doldururken gözü aynı yerde arıyor.
 *
 * Üst blok (firma adı, adresler, VD/TC) koçanda MATBAA BASKILI olduğu için
 * burada da salt okunur; ayarlardan beslenir. Elle doldurulan her şey girilebilir.
 *
 * Resmi belge koçandaki nüshadır; bu ekran onun sistemdeki kaydını üretir.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import { iv } from './invoiceTheme';
import { tutarYaziyla } from '../../utils/giderPusulasiService';
import { pusulaKalemiUret } from '../../utils/giderPusulasiKurallari';
import { subeVarsayilanAdres } from '../../utils/giderPusulasi';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const SATIR_SAYISI = 5;
const bosSatir = () => ({ isinMahiyeti: '', cinsi: '', miktar: '', birim: 'Adet', fiyat: '', tutar: '' });

/** Alış işleminden form taslağı. */
export const formFromIslem = (aday, settings = null, { musteri = null, sube = '' } = {}) => {
  const bugun = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });
  const kalemler = Array.from({ length: SATIR_SAYISI }, bosSatir);
  let kuralNotu = '';
  if (aday) {
    // Şartlar burada uygulanır: döviz altına çevrilir, kısmi ödemede ürün ve
    // miktar pusula tutarına göre hesaplanır (bkz. giderPusulasiKurallari).
    const kalem = pusulaKalemiUret(aday, settings);
    if (kalem) {
      kalemler[0] = {
        isinMahiyeti: kalem.isinMahiyeti,
        cinsi: kalem.cinsi,
        miktar: kalem.miktar,
        birim: kalem.birim,
        fiyat: kalem.fiyat,
        tutar: kalem.tutar
      };
      if (kalem.cevrildi) kuralNotu = kalem.sebep;
    }
  }
  return {
    kuralNotu,
    seri: 'B',
    sira: '',
    tarih: aday?.tarih || bugun,
    satici: {
      adSoyad: aday?.satici || '',
      // TCKN müşteri kartından gelir; adres boşsa şube varsayılanı yazılır.
      tcKimlikNo: String(musteri?.tcKimlikNo || '').replace(/\D/g, ''),
      adres: String(musteri?.adres || '').trim() || subeVarsayilanAdres(sube),
      vergiDairesi: '',
      vergiNo: ''
    },
    kalemler,
    tevkifat: { gelirVergisiOrani: '', gelirVergisiTutari: '', fonPayi: '' }
  };
};

/* ——— belge görünümü ——— */
const CERCEVE = '1px solid #1f2937';
const hucreSx = { border: CERCEVE, p: 0, height: 26 };
const girdiSx = {
  width: '100%', height: '100%', border: 0, outline: 'none', background: 'transparent',
  padding: '0 5px', fontSize: 12.5, fontFamily: 'inherit', color: '#111827', boxSizing: 'border-box'
};
const baslikSx = {
  border: CERCEVE, bgcolor: '#f3f4f6', fontSize: 10.5, fontWeight: 800,
  textAlign: 'center', py: 0.35, letterSpacing: 0.2
};
const etiketSx = { border: CERCEVE, fontSize: 10.5, fontWeight: 700, px: 0.6, display: 'flex', alignItems: 'center' };

function GiderPusulasiDialog({ open, aday, saving, settings, sube, musteriler, onClose, onSave }) {
  const musteri = aday?.musteriId ? musteriler?.[aday.musteriId] : null;
  const [form, setForm] = useState(() => formFromIslem(aday, settings, { musteri, sube }));
  const [hata, setHata] = useState('');

  useEffect(() => {
    if (open) {
      setForm(formFromIslem(aday, settings, { musteri, sube }));
      setHata('');
    }
  }, [open, aday, settings, musteri, sube]);

  const toplam = useMemo(
    () => round2((form.kalemler || []).reduce((t, k) => t + (Number(k.tutar) || 0), 0)),
    [form.kalemler]
  );
  const kesinti = useMemo(
    () => round2((Number(form.tevkifat?.gelirVergisiTutari) || 0) + (Number(form.tevkifat?.fonPayi) || 0)),
    [form.tevkifat]
  );
  const net = round2(toplam - kesinti);

  const setSatir = (i, alan, deger) => {
    setForm((p) => {
      const kalemler = [...p.kalemler];
      const satir = { ...kalemler[i], [alan]: deger };
      if (alan === 'miktar' || alan === 'fiyat') {
        satir.tutar = round2((Number(satir.miktar) || 0) * (Number(satir.fiyat) || 0));
      }
      kalemler[i] = satir;
      return { ...p, kalemler };
    });
  };
  const setTev = (alan, v) => setForm((p) => ({ ...p, tevkifat: { ...p.tevkifat, [alan]: v } }));
  const setSatici = (alan, v) => setForm((p) => ({ ...p, satici: { ...p.satici, [alan]: v } }));

  const girdi = (deger, onChange, ek = {}) => (
    <input value={deger ?? ''} onChange={(e) => onChange(e.target.value)} style={girdiSx} {...ek} />
  );

  const kaydet = async () => {
    setHata('');
    try {
      await onSave({ ...form, kalemler: form.kalemler.filter((k) => Number(k.tutar) > 0 || k.cinsi || k.isinMahiyeti) });
    } catch (e) {
      setHata(e?.message || 'Kaydedilemedi');
    }
  };

  const unvan = settings?.companyTitle || 'MEHMET DEMİRSOY';
  const vd = settings?.taxOffice || 'KÜÇÜKÇEKMECE';
  const tc = settings?.taxNumber || '33592419332';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent sx={{ bgcolor: '#fff', p: 2 }}>
        <Box sx={{ border: CERCEVE, p: 1.2, fontFamily: '"Inter", system-ui, sans-serif', color: '#111827' }}>

          {/* ÜST BLOK — koçanda matbaa baskılı */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 1, alignItems: 'start', pb: 0.8 }}>
            <Box>
              <Typography sx={{ fontSize: 19, fontWeight: 800, letterSpacing: 2 }}>UKBA ✦ GOLD</Typography>
              <Typography sx={{ fontSize: 12.5, fontWeight: 800, mt: 0.3 }}>{unvan}</Typography>
              <Typography sx={{ fontSize: 9.5, lineHeight: 1.35, mt: 0.2 }}>
                <b>Şube:</b> Kavaklı Mahallesi Oğuzhan Caddesi No:32/D Beylikdüzü / İSTANBUL · Tel: 0212 302 34 44
              </Typography>
              <Typography sx={{ fontSize: 9.5, lineHeight: 1.35 }}>
                <b>Merkez:</b> F. Çakmak Mah. Sultan Murat Cad. No: 64/17 Küçükçekmece / İSTANBUL · Tel: 0212 426 35 21
              </Typography>
              <Typography sx={{ fontSize: 9.5, lineHeight: 1.35 }}>{vd} V.D. TC. Kim. No: {tc}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', pt: 0.5 }}>
              <Box sx={{ width: 58, height: 58, borderRadius: '50%', border: '1.5px solid #374151',
                display: 'grid', placeItems: 'center', fontSize: 7.5, fontWeight: 700, lineHeight: 1.1, color: '#374151' }}>
                T.C.<br />HAZİNE VE<br />MALİYE<br />BAKANLIĞI
              </Box>
              <Typography sx={{ fontSize: 10, fontWeight: 800, mt: 0.4 }}>İL KODU: 34</Typography>
            </Box>
            <Box sx={{ textAlign: 'right', minWidth: 170 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 800, letterSpacing: 0.5 }}>GİDER PUSULASI</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, mt: 0.6 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700 }}>SERİ :</Typography>
                <Box sx={{ width: 42, borderBottom: CERCEVE }}>{girdi(form.seri, (v) => setForm((p) => ({ ...p, seri: v })), { style: { ...girdiSx, textAlign: 'center', fontWeight: 700 } })}</Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700 }}>SIRA</Typography>
                <Box sx={{ width: 92, borderBottom: CERCEVE }}>
                  {girdi(form.sira, (v) => setForm((p) => ({ ...p, sira: v })), { placeholder: '998345', style: { ...girdiSx, textAlign: 'center', fontWeight: 800, color: '#b91c1c', fontSize: 14 } })}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, mt: 0.6 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700 }}>Tarih</Typography>
                <Box sx={{ width: 118, borderBottom: CERCEVE }}>{girdi(form.tarih, (v) => setForm((p) => ({ ...p, tarih: v })), { type: 'date', style: { ...girdiSx, fontSize: 12 } })}</Box>
              </Box>
            </Box>
          </Box>

          {/* KALEM TABLOSU */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1.5fr 1.7fr 0.9fr 1fr 1.2fr' }}>
            {['İŞİN MAHİYETİ', 'CİNSİ', 'ADEDİ / GRAM', 'FİYATI', 'TUTAR'].map((h) => (
              <Box key={h} sx={baslikSx}>{h}</Box>
            ))}
            {form.kalemler.map((k, i) => (
              <React.Fragment key={i}>
                <Box sx={hucreSx}>{girdi(k.isinMahiyeti, (v) => setSatir(i, 'isinMahiyeti', v))}</Box>
                <Box sx={hucreSx}>{girdi(k.cinsi, (v) => setSatir(i, 'cinsi', v))}</Box>
                <Box sx={hucreSx}>{girdi(k.miktar, (v) => setSatir(i, 'miktar', v), { type: 'number', style: { ...girdiSx, textAlign: 'center' } })}</Box>
                <Box sx={hucreSx}>{girdi(k.fiyat, (v) => setSatir(i, 'fiyat', v), { type: 'number', style: { ...girdiSx, textAlign: 'right' } })}</Box>
                <Box sx={hucreSx}>{girdi(k.tutar, (v) => setSatir(i, 'tutar', v), { type: 'number', style: { ...girdiSx, textAlign: 'right', fontWeight: 700 } })}</Box>
              </React.Fragment>
            ))}
          </Box>

          {/* TOPLAM + TEVKİFAT — sağa yaslı, koçandaki gibi */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1.5fr 1.7fr 0.9fr 1fr 1.2fr' }}>
            <Box sx={{ gridColumn: '1 / 3' }} />
            <Box sx={{ gridColumn: '3 / 5', ...etiketSx, justifyContent: 'center', fontWeight: 800, height: 26 }}>TOPLAM</Box>
            <Box sx={{ ...hucreSx, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', px: 0.6, fontSize: 12.5, fontWeight: 800 }}>
              {toplam ? toplam.toLocaleString('tr-TR') : ''}
            </Box>

            {[
              ['GELİR VERGİSİ ORANI %', 'gelirVergisiOrani'],
              ['TUTARI', 'gelirVergisiTutari'],
              ['FON PAYI', 'fonPayi']
            ].map(([etiket, alan], idx) => (
              <React.Fragment key={alan}>
                {idx === 0 ? (
                  <Box sx={{ gridColumn: '1 / 3', gridRow: 'span 4', border: CERCEVE, display: 'grid', placeItems: 'center' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 800, writingMode: 'vertical-rl', transform: 'rotate(180deg)', letterSpacing: 1 }}>
                      TEVKİF EDİLEN
                    </Typography>
                  </Box>
                ) : null}
                <Box sx={{ gridColumn: '3 / 5', ...etiketSx, height: 24 }}>{etiket}</Box>
                <Box sx={{ ...hucreSx, height: 24 }}>{girdi(form.tevkifat[alan], (v) => setTev(alan, v), { type: 'number', style: { ...girdiSx, textAlign: 'right' } })}</Box>
              </React.Fragment>
            ))}
            <Box sx={{ gridColumn: '3 / 5', ...etiketSx, height: 24 }}>KESİNTİ TOPLAMI</Box>
            <Box sx={{ ...hucreSx, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', px: 0.6, fontSize: 12.5 }}>
              {kesinti ? kesinti.toLocaleString('tr-TR') : ''}
            </Box>

            <Box sx={{ gridColumn: '1 / 3' }} />
            <Box sx={{ gridColumn: '3 / 5', ...etiketSx, fontWeight: 800, height: 26 }}>ÖDENECEK NET TUTAR</Box>
            <Box sx={{ ...hucreSx, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', px: 0.6, fontSize: 13.5, fontWeight: 800 }}>
              {net ? net.toLocaleString('tr-TR') : ''}
            </Box>
          </Box>

          {/* ALT BLOK */}
          <Box sx={{ mt: 1.6, display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Yalnız:</Typography>
            <Box sx={{ flex: 1, borderBottom: CERCEVE, fontSize: 12.5, fontStyle: 'italic', minHeight: 20, px: 0.5 }}>
              {net ? tutarYaziyla(net) : ''}
            </Box>
          </Box>
          <Box sx={{ mt: 0.8, display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Box sx={{ width: 230, borderBottom: CERCEVE }} />
            <Typography sx={{ fontSize: 10.5 }}>’den yukarıda belirtilen (MAL/İŞ) bedelini aldım.</Typography>
          </Box>

          <Box sx={{ mt: 1.2, display: 'grid', gridTemplateColumns: '1fr 150px', gap: 1 }}>
            <Box>
              {[
                ['Adı, Soyadı', 'adSoyad', ''],
                ['Adresi', 'adres', ''],
                ['TC. Kim. No', 'tcKimlikNo', ''],
                ['Vergi Da. ve No.su', 'vergiDairesi', '']
              ].map(([etiket, alan]) => (
                <Box key={alan} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.8, mb: 0.45 }}>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, minWidth: 110 }}>{etiket} :</Typography>
                  <Box sx={{ flex: 1, borderBottom: CERCEVE }}>{girdi(form.satici[alan], (v) => setSatici(alan, v), { style: { ...girdiSx, fontSize: 12 } })}</Box>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'grid', placeItems: 'end center' }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700 }}>İMZA</Typography>
            </Box>
          </Box>
        </Box>

        {form.kuralNotu ? (
          <Typography sx={{ mt: 1, fontSize: 12.5, fontWeight: 700, color: '#92400e', bgcolor: '#fef3c7', px: 1, py: 0.6, borderRadius: 1 }}>
            Şart uygulandı: {form.kuralNotu}
          </Typography>
        ) : null}
        {hata ? <Typography sx={{ mt: 1, fontSize: 13, fontWeight: 700, color: iv.danger }}>{hata}</Typography> : null}
        <Typography sx={{ mt: 1, fontSize: 11.5, color: iv.textMuted }}>
          Resmi belge koçandaki nüshadır. Burada yalnız kaydı tutulur; seri ve sıra numarasını koçandan yazın.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700 }}>Vazgeç</Button>
        <Button variant="contained" disabled={saving} onClick={kaydet}
          sx={{ textTransform: 'none', fontWeight: 700, bgcolor: iv.primary, '&:hover': { bgcolor: iv.primary } }}>
          Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default GiderPusulasiDialog;
